import moment from "moment/min/moment-with-locales";
import { useEffect, useMemo, useState } from "react";
import { Col, Form, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useLoaderData } from "react-router-dom";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { getTrendLabel } from "../components/HelperFunctions";
import { useFetchData } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker from "../components/Pickers/DatesPicker";
import HybridHitlistPicker from "../components/Pickers/HybridHitlistPicker";
import { useUserFormState } from "../components/UserSelection";
import HybridHitlistHeader from "../components/hybridHitlist/HybridHitlistHeader";
import HybridHitlistRow from "../components/hybridHitlist/HybridHitlistRow";
import { DataContent, InputSelection, ModulePage, RATable, SearchWithDownloadRow, SoleDescriptionRow } from "../components/pageSections";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";
import { useHybridHitlistTrendModal } from "../components/hybridHitlist/HybridHitlistTrend";

const extraDefaults = { hitlist: [], digital: false };

export default function HybridHitlist() {
    const moduleName = "hybridHitlist";
    const { latestHybridData, latestDigitalData } = useLoaderData();
    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [filterData, setFilterData] = useState({ searchString: "" });
    const [submittedFormData, setSubmittedFormData] = useState(null);

    const { setTrendSong, HybridHitlistTrendModalComponent } = useHybridHitlistTrendModal();
    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();

    useEffect(() => {
        async function setLatestDate() {
            let baseDate;
            if (formData.digital) {
                const resolvedDates = (await latestDigitalData).data;
                baseDate = resolvedDates.find(x => x.digital_hitlist_id === formData.hitlist[0].digital_hitlist_id)?.latest_date;
            } else {
                const resolvedDates = (await latestHybridData).data;
                baseDate = resolvedDates.find(x => x.hybrid_hitlist_id === formData.hitlist[0].hybrid_hitlist_id)?.latest_date;
            }
            baseDate = moment(baseDate);
            setLatestCalculatedDate(baseDate.clone().endOf("isoWeek"));
            const defaultRange = [baseDate.clone().toDate(), baseDate.clone().endOf("isoWeek").toDate()];
            if (formData.dateRange === undefined || (formData.dateRange && formData.dateRange[1] > defaultRange[1])) {
                setFormData(current => ({ ...current, dateRange: defaultRange }));
            }
        }
        if (latestHybridData && latestDigitalData && formData.hitlist.length) setLatestDate();
        else setLatestCalculatedDate(null);
    }, [formData.digital, formData.dateRange, formData.hitlist, latestHybridData, latestDigitalData, setFormData]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.hitlist[0].radio_id);
        const hitlistId = formData.digital ? formData.hitlist[0].digital_hitlist_id : formData.hitlist[0].hybrid_hitlist_id;
        const hitlistType = formData.digital ? "digital" : "hybrid";
        const basis_week = moment(formData.dateRange[0]).format("YYYY-MM-DD");
        const url = `/hybrid_hitlist/${hitlistType}/${hitlistId}/${basis_week}`;
        await fetchData(url);
        setSubmittedFormData(formData);
    }

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.song.artist.toLowerCase();
                    const title = x.song.title.toLowerCase();
                    const year = x.song.release_year?.toString() ?? "";

                    return andParts.every(part => {
                        let orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                const regex = new RegExp(`\\b${exact}\\b`, "i");
                                return regex.test(artist) || regex.test(title) || year === exact;
                            }

                            return artist.includes(term) || title.includes(term) || year.startsWith(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo);

    const columns = useMemo(() => {
        const base = [
            { header: "Rank", value: x => x.song_rank },
            { header: "Artist", value: x => x.song.artist },
            { header: "Title", value: x => x.song.title },
            { header: "Release Year", value: x => x.song.release_year },
        ];
        const hybrid = [
            { header: "Digital Rank", value: x => x.digital_rank },
            { header: "Airplay Rank", value: x => x.airplay_rank },
        ];

        const trends = [
            { header: "Trend", value: x => x.rank_trend },
            { header: "Trend Label", value: x => getTrendLabel(x.rank_trend != null ? Math.sign(x.rank_trend) : null) },
        ];

        return formData.digital ? [...base, ...trends] : [...base, ...hybrid, ...trends];
    }, [formData.digital]);

    const csvHeaders = useMemo(() => columns.map(c => c.header), [columns]);
    const csvData = useMemo(() => sortedItems?.map(x => columns.map(c => c.value(x))), [sortedItems, columns]);

    return (
        <ModulePage>
            <ModulePage.Title>Hybrid Hitlist</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.hitlist.length || !formData.dateRange}>
                <InputSelection.Main>
                    <HybridHitlistPicker formData={formData} setFormData={setFormData} name={moduleName} loadOnChange={loadFormData} />
                    <DatesPicker
                        formData={formData}
                        setFormData={setFormData}
                        latestAvailableDate={latestCalculatedDate}
                        disabled={!formData.hitlist.length}
                        predefinedRanges={[]}
                        hoverRange={"week"}
                        showWeekNumbers
                        oneTap
                    />
                    <Form.Group as={Col} className="d-flex flex-column col-12 col-xl-3">
                        <Form.Label>Mix with airplay</Form.Label>
                        <ToggleButtonGroup
                            type="radio"
                            name="hitlist_type"
                            value={!formData.digital}
                            onChange={value => setFormData({ ...formData, digital: !value })}
                            className=" ra-switcher">
                            <ToggleButton id="hitlist-hybrid" variant="ra-dark-blue-outline" value={true}>
                                Yes
                            </ToggleButton>
                            <ToggleButton id="hitlist-digital" variant="ra-dark-blue-outline" value={false}>
                                No
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Form.Group>
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Hybrid Hitlist"}
                        whatList={[
                            "Get a “digital” airplay chart, showing what music is being listened to across a large panel of online sources",
                            "Mix your “digital” airplay results with your regional market airplay to get an early warning chart for upcoming hits",
                            "Choose from a number of different countries and formats",
                        ]}
                        knowList={[
                            {
                                text: "Hybrid Hitlist runs every week, and displays the most recent week of data",
                            },
                            { text: "Sources are Apple Music, Spotify, Youtube Music, Deezer, Shazam, and more" },
                            { text: "Open the song catalogue by clicking on the row of a song" },
                        ]}
                        videoSrc="https://www.youtube.com/embed/O1UzpcRKUWU?si=XL8L-nYuRDynFE_E"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <SoleDescriptionRow>
                            <DataDescription>
                                <DataDescription.Simple label="Hitlist" value={submittedFormData?.hitlist[0].name} />
                                <DataDescription.Simple label="Type" value={submittedFormData?.digital ? "Digital" : "Mixed"} />
                                <DataDescription.DateRange startDate={submittedFormData?.dateRange[0]} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <SearchWithDownloadRow
                            filterData={filterData}
                            setFilterData={setFilterData}
                            data={csvData}
                            csvHeaders={csvHeaders}
                            filename={`Hybrid Hitlist ${submittedFormData?.hitlist[0].name} ${submittedFormData?.digital ? "Digital" : "Mixed"}`}
                        />
                        <RATable>
                            <RATable.Header>
                                <HybridHitlistHeader requestSort={requestSort} sortConfig={sortConfig} digital={submittedFormData?.digital} />
                            </RATable.Header>
                            <RATable.Body>
                                {!sortedItems?.length ? (
                                    <EmptyRow text="No songs found" />
                                ) : (
                                    sortedItems.map(x => (
                                        <HybridHitlistRow
                                            key={x.song.item_id}
                                            item={x}
                                            setTrendSong={setTrendSong}
                                            setCatalogueSong={setCatalogueSong}
                                            digital={submittedFormData?.digital}
                                        />
                                    ))
                                )}
                            </RATable.Body>
                        </RATable>
                    </>
                )}
                <HybridHitlistTrendModalComponent
                    hybridHitlistId={!submittedFormData?.digital && submittedFormData?.hitlist[0].hybrid_hitlist_id}
                    hitlistName={submittedFormData?.hitlist[0]?.name}
                />
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
